import * as _ from "lodash";
import { nameofFactory, parseEnumStart } from "./utils";

// Options for Safety parameter data types
export const safetyDatatypeOptions = [
  {
    key: 1,
    text: "TYPE_BOOL",
    value: 1
  },
  {
    key: 2,
    text: "TYPE_UINT8",
    value: 2
  },
  {
    key: 3,
    text: "TYPE_UINT16",
    value: 3
  },
  {
    key: 4,
    text: "TYPE_SINT16",
    value: 4
  },
  {
    key: 5,
    text: "TYPE_UINT32",
    value: 5
  }
];

// Options for Drive's parameter visibility
export const visibilityOptions = [
  {
    key: 0,
    text: "Special (R&D) (0)",
    value: 0
  },
  {
    key: 1,
    text: "Default set (1)",
    value: 1
  },
  {
    key: 2,
    text: "Limited (Novice) (2)",
    value: 2
  },
  {
    key: 7,
    text: "Not in use (7)",
    value: 7
  }
];

// Media types used in pdApi. pdApi requires that in case of fault and parameter PUT and POST contain
// correct media type.
export const mediaTypes = {
  liftFault: "application/x.lift-fault+json",
  apmFault: "application/x.apm-fault+json",
  driveFault: "application/x.drive-fault+json",
  groupFault: "application/x.group-fault+json",
  networkFault: "application/x.network-fault+json",
  monitoringFault: "application/x.monitoring-fault+json",
  nonKoneFault: "application/x.nonkone-fault+json",
  signalizationFault: "application/x.signalization-fault+json",
  liftParameter: "application/x.lift-parameter+json",
  driveParameter: "application/x.drive-parameter+json",
  groupParameter: "application/x.group-parameter+json",
  safetyParameter: "application/x.safety-parameter+json",
  apmParameter: "application/x.apm-parameter+json"
};

// Type for dropdown
export type DropdownOption = {
  text: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  key?: number | string | null;
};

// Internal type for handling value data format
export type ValueDataFormatType = {
  range: "Number" | "Special";
  mode?: "Editable" | "Display only";
  type?: "Signed" | "Unsigned";
  digits?: number;
  decimals?: number;
  value: number;
};

export const parameterFields = nameofFactory<Api.BaseParameter>();
export const driveParameterFields = nameofFactory<Api.DriveParameter>();
export const liftParameterFields = nameofFactory<Api.LiftParameter>();
export const groupParameterFields = nameofFactory<Api.GroupParameter>();
export const driveSignalGroupFields = nameofFactory<Api.DriveSignalGroup>();
export const escalatorRTDGroupFields = nameofFactory<Api.EscalatorRTDGroup>();
export const escalatorParameterFields = nameofFactory<Api.EscalatorParameter>();
export const escalatorEventLogFields = nameofFactory<Api.EscalatorEventLog>();
export const doorParameterFields = nameofFactory<Api.DoorParameter>();

// Get group name of given domain id
export const getGroupNameByDomainId = (domainId: number, pdApi: State.PdApi): Api.GroupName => {
  const { domains, domainGroups } = pdApi;
  const groupId = domains[domainId]?.domainGroup.id;
  const groupName = domainGroups[groupId].enumName;
  return groupName as Api.GroupName;
};

// Get domain enumvalue of given domain id
export const getDomainEnumValueById = (domainId: number, pdApi: State.PdApi): number => {
  const { domains } = pdApi;
  const enumValue = domains[domainId].enumValue;
  return enumValue;
};

// Get media type of fault by domain id
export const getFaultMediaType = (domainId: number, pdApi: State.PdApi): string => {
  const groupName = getGroupNameByDomainId(domainId, pdApi);
  switch (groupName) {
    case "Lift":
      return mediaTypes.liftFault;
    case "APM":
      return mediaTypes.apmFault;
    case "Drive":
      return mediaTypes.driveFault;
    case "NonKone":
      return mediaTypes.nonKoneFault;
    case "Signalization":
      return mediaTypes.signalizationFault;
    case "Network":
      return mediaTypes.networkFault;
    case "Monitoring":
      return mediaTypes.monitoringFault;
    default:
      return mediaTypes.groupFault;
  }
};

// Get media type of parameter by domain id
export const getParameterMediaType = (domainId: number, pdApi: State.PdApi): string => {
  const groupName = getGroupNameByDomainId(domainId, pdApi);
  switch (groupName) {
    case "Lift":
      return mediaTypes.liftParameter;
    case "Drive":
      return mediaTypes.driveParameter;
    case "Safety":
      return mediaTypes.safetyParameter;
    case "APM":
      return mediaTypes.apmParameter;
    default:
      return mediaTypes.groupParameter;
  }
};

export const getMediaType = (
  option: Api.OptionsGrud,
  editedItem: Api.AnyItem,
  pdApi: State.PdApi
): string => {
  switch (option) {
    case "faults": {
      const editedFault = editedItem as Api.AnyFault;
      return getFaultMediaType(editedFault.domain.id, pdApi);
    }
    case "parameters": {
      const editedParameter = editedItem as Api.AnyParameter;
      return getParameterMediaType(editedParameter.domain.id, pdApi);
    }
    default:
      return "application/json";
  }
};

export const createEmptyKCEProduct = (): Api.KceProduct => {
  const newKCEProduct: Api.KceProduct = {
    id: -1,
    url: "",
    productNumber: 0,
    productName: "",
    comment: "",
    created: "",
    released: "",
    obsoleted: ""
  };
  return newKCEProduct;
};

export const createEmptyKCEVersion = (): Api.KceVersion => {
  const newKCEVersion: Api.KceVersion = {
    id: -1,
    url: "",
    number: "",
    name: "",
    comment: ""
  };
  return newKCEVersion;
};

export const createEmptyDriveVersion = (): Api.DriveVersion => ({
  id: 0,
  url: "",
  week: 0,
  year: 0,
  description: "",
  domain: {
    /* This is domain KDL16 which seems to be one of two used domains */
    id: 3,
    url: ""
  }
});

export const createEmptyDoorVersion = (): Api.DoorVersion => {
  const newKCEVersion: Api.DoorVersion = {
    versionId: -1,
    url: "",
    number: "",
    versionName: "",
    description: "",
    domain: {
      id: -1,
      url: ""
    }
  };
  return newKCEVersion;
};

export const createEmptyEscalatorVersion = (): Api.EscalatorVersion => {
  const newEscalatorVersion: Api.EscalatorVersion = {
    id: -1,
    number: "",
    versionId: -1,
    url: "",
    versionName: "",
    description: "",
    domain: {
      id: -1,
      url: ""
    }
  };
  return newEscalatorVersion;
};

export const createEmptyDomain = (): Api.Domain | undefined => {
  const newDomain: Api.Domain = {
    id: -1,
    url: "",
    domainGroup: {
      id: 1,
      url: ""
    },
    enumValue: 0,
    enumName: "",
    swComment: ""
  };
  return newDomain;
};

// Grand Norm are only editable under the Elevator menu, so new grand norm
// default to the Lift domain group (forced again by EditGrandNorm on mount).
export const createEmptyGrandNorm = (pdApi?: State.PdApi): Api.GrandNorm | undefined => {
  const liftDomainGroup = pdApi
    ? _.values(pdApi.domainGroups).find(group => group.enumName === "Lift")
    : undefined;
  const newGrandNorm: Api.GrandNorm = {
    id: -1,
    code: 0,
    name: "",
    domainGroupId: liftDomainGroup?.enumValue ?? 0,
    domainGroupName: liftDomainGroup?.enumName ?? ""
  };
  return newGrandNorm;
};

export const createEmptyParameterCategory = (): Api.ParameterCategory | undefined => {
  const newParameterCategory: Api.ParameterCategory = {
    id: -1,
    url: "",
    domainGroup: {
      id: 1,
      url: ""
    },
    nameTranslationKey: "",
    descriptionTranslationKey: "",
    description: "",
    enumValue: 0,
    enumName: "",
    name: "",
    domain: {
      id: -1,
      url: ""
    },
    parentCategory: null,
    releaseStatus: ""
  };
  return newParameterCategory;
};

// Create default empty Fault Component
export const createEmptyFaultComponent = (
  pdApi?: State.PdApi
): Api.FaultComponentEnumeration | undefined => {
  const unknownDomainGroup = pdApi
    ? _.values(pdApi.domainGroups).find(group => group.enumName === "Unknown")
    : undefined;
  const unknownDomainGroupId = unknownDomainGroup?.id ?? 1;

  const newEnumeration: Api.FaultComponentEnumeration = {
    enumValue: 0,
    enumName: "",
    faultRangeMin: 0,
    faultRangeMax: 0,
    comment: "",
    name: "",
    domainGroup: {
      id: unknownDomainGroupId,
      url: ""
    }
  };
  return newEnumeration;
};

// Create default empty fault category
export const createEmptyFaultCategory = (): Api.FaultCategory => {
  const newFaultCategory: Api.FaultCategory = {
    id: 0,
    url: "",
    enumValue: 0,
    enumName: "",
    comment: "",
    name: "",
    nameTranslationKey: "",
    locs: [],
    createdBy: null,
    createdDate: null,
    modifiedBy: null,
    modifiedDate: null
  };
  return newFaultCategory;
};

// Create default empty fault
export const createEmptyFault = (
  pdApi: State.PdApi,
  groupName: Api.GroupName
): Api.AnyFault | undefined => {
  const { faultPriorities, domains, domainGroups, faultCategories, kceVersions } = pdApi;
  const group = _.values(pdApi.domainGroups).find(
    domainGroup => domainGroup.enumName === groupName
  );
  if (group) {
    // Backend currently does not support localized priorities for non-kone faults.
    // Made a temporary solution of "borrowing" fault priorities from lift.
    // TODO: make a proper solution for this (in backend too)
    const liftDomainGroupName = "Lift";
    const liftDomainGroupId = _.values(domainGroups).find(
      group => group.enumName === liftDomainGroupName
    )?.id;

    const faultPrioritiesList = _.values(faultPriorities).filter(faultPriority =>
      group.enumName !== "NonKone"
        ? faultPriority.domainGroup.id === group.id
        : faultPriority.domainGroup.id === liftDomainGroupId
    );
    
    // Fallback: if no priorities found for this group, use Lift group priorities
    const finalPrioritiesList = faultPrioritiesList.length > 0 
      ? faultPrioritiesList 
      : _.values(faultPriorities).filter(faultPriority => faultPriority.domainGroup.id === liftDomainGroupId);
    
    const domainsList = _.values(domains).filter(domain => domain.domainGroup.id === group.id);
    const faultCategoryList = _.values(faultCategories);
    const kceVersionsList = _.values(kceVersions);

    const fault: Api.BaseFault = {
      code: "0",
      name: "",
      detection: "",
      operation: "",
      recovery: "",
      comment: "",
      englishName: "",
      nameTranslationKey: "",
      reason: "",
      reasonTranslationKey: "",
      action: "",
      actionTranslationKey: "",
      domain: domainsList[0],
      priority: finalPrioritiesList[0],
      permissions: [],
      locs: []
    };
    if (groupName === "Drive") {
      const driveFault: Api.DriveFault = {
        ...fault,
        faultCategory: faultCategoryList[0],
        data1Description: "",
        data2Description: "",
        testing: "",
        supervisionAdjustable: false,
        localizable: false
      };
      return driveFault;
    } else if (groupName === "Lift") {
      const liftFault: Api.LiftFault = {
        ...fault,
        enumName: "",
        setupReq: false,
        manualReset: false,
        startPrevented: false,
        scOpen: false,
        changeBoard: false,
        codeVariant: "",
        additionalInfoDef: "",
        validFromVersion: kceVersionsList[0],
        validUntilVersion: kceVersionsList[0],
        dataActId: 1,
        grandNormId: 1
      };
      return liftFault;
    } else if (groupName === "APM") {
      const apmFault: Api.APMFault = {
        ...fault,
        enumName: "",
        setupReq: false,
        manualReset: false,
        startPrevented: false,
        scOpen: false,
        changeBoard: false,
        codeVariant: "",
        additionalInfoDef: "",
        validFromVersion: kceVersionsList[0],
        validUntilVersion: kceVersionsList[0],
        dataActId: 1
      };
      return apmFault;
    } else if (groupName === "Group") {
      const groupFault: Api.GroupFault = {
        ...fault,
        enumName: "",
        releaseStatus: ""
      };
      return groupFault;
    } else if (groupName === "Network") {
      const networkFault: Api.NetworkFault = {
        ...fault,
        enumName: "",
        data1Description: "",
        data2Description: "",
        testing: ""
      };
      return networkFault;
    } else if (groupName === "Monitoring") {
      const monitoringFault: Api.MonitoringFault = {
        ...fault,
        faultCategory: { id: 1, url: "" },
        data1Description: "",
        data2Description: "",
        data3Description: "",
        data4Description: "",
        supervisionAdjustable: false,
        localizable: false
      };
      return monitoringFault;
    } else if (groupName === "NonKone") {
      const nonKoneFault: Api.NonKoneFault = {
        ...fault,
        code: ""
      };
      return nonKoneFault;
    } else if (groupName === "Escalator") {
      const escalatorFault: Api.EscalatorFault = {
        ...fault,
        enumName: "",
        code: "",
        priority: faultPrioritiesList[0],
        dataActId: 1
      };
      return escalatorFault;
    } else if (groupName === "Door") {
      const doorFault: Api.DoorFault = {
        ...fault,
        code: "",
        enumName: "",
        priority: faultPrioritiesList[0]
      };
      return doorFault;
    } else if (groupName === "Signalization") {
      const signalizationFault: Api.SignalizationFault = {
        ...fault,
        enumName: "",
        releaseStatus: ""
      };
      return signalizationFault;//check and confirm if this is in sync
    }

    return fault as Api.AnyFault;
  } else {
    return undefined;
  }
};

// Create default empty parameter
export const createEmptyParameter = (
  pdApi: State.PdApi,
  groupName: Api.GroupName,
  // eslint-disable-next-line
  apm: boolean
): Api.AnyParameter | undefined => {
  const { domains, parameterCategories } = pdApi;
  const group = _.values(pdApi.domainGroups).find(
    domainGroup => domainGroup.enumName === groupName
  );

  if (group) {
    const parameterCategoriesList = _.values(parameterCategories);

    const parameterCategoriesFiltered = parameterCategoriesList.filter(
      faultCategory => faultCategory.domainGroup.id === group.id
    );

    const domainsList = _.values(domains).filter(domain => domain.domainGroup.id === group.id);

    let defaultDomainIndex = domainsList.findIndex(d => d.domainGroup.id === group.id);
    if (defaultDomainIndex < 0) {
      defaultDomainIndex = 0;
    }

    const parameterCategory =
      parameterCategoriesFiltered.length > 0
        ? groupName !== "APM"
          ? parameterCategoriesFiltered[0]
          : null
        : null;

    const parameter: Api.BaseParameter = {
      id: 0,
      code: 0,
      url: "",
      name: "",
      nameTranslationKey: "",
      domain: domainsList[defaultDomainIndex],
      parameterCategory,
      enumName: "",
      description: "",
      descriptionTranslationKey: "",
      locs: []
    };
    if (groupName === "Drive") {
      const driveParameter: Api.DriveParameter = {
        ...parameter,
        unitName: "",
        valueDataFormat: 0,
        swDefaultValue: 0,
        shaftSetupRequired: false,
        customMotorType: false,
        changeMotorType: false,
        rideComfort: false,
        triggerFunction: "",
        extraScaling: undefined,
        visibility: 0,
        permissions: [],
        localizable: false,
        storeIndex: 0,
        preferredValueTypeEnumValue: 2,
        preferredValues: [],
        driveParameterBindings: []
      };
      return driveParameter;
    } else if (groupName === "Lift") {
      const liftParameter: Api.LiftParameter = {
        ...parameter,
        itemCount: 1,
        minValue: 0,
        maxValue: 0,
        requiresReset: false,
        requiresSetup: false,
        vectorBinding: 0,
        permissions: [],
        editorDef: null,
        comment: "",
        swDefaultValue: "0",
        created: new Date(Date.now()).toISOString(),
        createdBy: "TODO: add real user",
        liftParameterValues: [],
        liftParameterBindings: []
      };
      return liftParameter;
    } else if (groupName === "Group") {
      const groupParameter: Api.GroupParameter = {
        ...parameter,
        featureDbId: 0,
        releaseStatus: ""
      };
      return groupParameter;
    } else if (groupName === "Escalator") {
      const escalatorParameter: Api.EscalatorParameter = {
        ...parameter,
        comment: "",
        requiresReset: false,
        requiresSetup: false,
        paramInspection: false,
        accessInRunning: false,
        paramType: 0,
        paramFormat: 0,
        vectorBinding: 0,
        permissions: [],
        unitName: "",
        valueDataFormat: 0,
        swDefaultValue: 0,
        preferredValueTypeEnumValue: 2,
        preferredValues: [],
        escalatorParameterBindings: [],
        escalatorParamTypeEnum: "",
        escalatorParamFormatEnum: ""
      };
      return escalatorParameter;
    } else if (groupName === "Door") {
      const doorParameter: Api.DoorParameter = {
        ...parameter,
        comment: "",
        requiresReset: false,
        requiresLearning: false,
        vectorBinding: 0,
        permissions: [],
        unitName: "",
        valueDataFormat: 0,
        swDefaultValue: 0,
        doorParameterBindings: []
      };
      return doorParameter;
    } else if (groupName === "APM") {
      const apmParameter: Api.ApmParamters = {
        ...parameter,
        featureDbId: 2,
        releaseStatus: ""
      };
      return apmParameter;
    } else if (groupName === "Safety") {
      const safetyParameter: Api.SafetyParameter = {
        ...parameter,
        minValue: 0,
        maxValue: 1,
        unit: "",
        byteCount: 0,
        isSubTitleRow: false,
        readOnly: false,
        factor: 0,
        byteIndex: 0,
        dataType: 0,
        bitMask: "0x00"
      };
      return safetyParameter;
    }

    return parameter as Api.AnyParameter;
  } else {
    return undefined;
  }
};

// create empty default user
export const createEmptyUser = (pdApi: State.PdApi): Api.User => {
  const { roles } = pdApi;
  let newUser: Api.User;
  let newRoles: Api.Role[];
  // Read access is granted by default to all new users
  const readRole = roles.find(role => role.name === "read") as Api.Role;
  // eslint-disable-next-line prefer-const
  newRoles = [readRole];
  // eslint-disable-next-line prefer-const
  newUser = {
    id: 0,
    url: "",
    adAccount: "",
    roles: newRoles as [Api.Role]
  };
  return newUser;
};
// create empty AAD group
export const createEmptyAADGroup = (pdApi: State.PdApi): Api.AADGroup => {
  const { roles } = pdApi;
  let newAADGroup: Api.AADGroup;
  let newRoles: Api.Role[];
  // Read access is granted by default to all new users
  const readRole = roles.find(role => role.name === "read") as Api.Role;
  // eslint-disable-next-line prefer-const
  newRoles = [readRole];
  // eslint-disable-next-line prefer-const
  newAADGroup = {
    id: 0,
    name: "",
    roles: newRoles as [Api.Role]
  };
  return newAADGroup;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createEmptyDriveSignal = (pdApi: State.PdApi): Api.DriveSignal => {
  const newDriveSignal: Api.DriveSignal = {
    id: 0,
    signalGroupEnumValue: 1,
    signalGroupEnumName: "",
    code: 0,
    swName: "",
    unitName: "",
    localizable: false,
    name: "",
    nameTranslationKey: "",
    description: "",
    descriptionTranslationKey: ""
  };
  return newDriveSignal;
};
export const createEmptyDriveTest = (): Api.DriveTest => {
  const newDriveTest: Api.DriveTest = {
    id: 0,
    code: 0,
    swName: "",
    name: "",
    description: "",
    localizable: false,
    locs: []
  };
  return newDriveTest;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createEmptyEscalatorRTD = (pdApi: State.PdApi): Api.EscalatorRTD => {
  const newEscalatorRTD: Api.EscalatorRTD = {
    id: 0,
    rtdGroupEnumValue: 0,
    rtdGroupEnumName: "",
    code: 0,
    swName: "",
    unitName: "",
    localizable: false,
    name: "",
    nameTranslationKey: "",
    description: "",
    descriptionTranslationKey: ""
  };
  return newEscalatorRTD;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createEmptyEscalatorRTDGroup = (pdApi: State.PdApi): Api.EscalatorRTDGroup => {
  const newEscalatorRTDGroup = {
    id: 0,
    enumValue: 0,
    enumName: "",
    description: "",
    name: "",
    nameTranslationKey: "",
    locs: []
  };
  return newEscalatorRTDGroup;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createEmptyEscalatorEventLog = (pdApi: State.PdApi): Api.EscalatorEventLog => {
  const newEscalatorEventLog: Api.EscalatorEventLog = {
    id: 0,
    eventCode: 0,
    eventName: "",
    data1EnumValue: 0,
    data1EnumName: "",
    data1UnitName: "",
    data1DecimalDigits: 0,
    data2EnumValue: 0,
    data2EnumName: "",
    data2UnitName: "",
    data2DecimalDigits: 0,
    name: "",
    nameTranslationKey: "",
    description: "",
    descriptionTranslationKey: ""
  };
  return newEscalatorEventLog;
};

export const createEmptyEventEnum = (): Api.EventEnumeration => {
  const newEnum: Api.EventEnumeration = {
    enumName: "",
    enumValue: 0,
    comment: "",
    eventClassEnumName: "None",
    escalatorEventClassId: null
  };
  return newEnum;
};

export const createEmptyEventType = (): Api.EventTypesEnum => {
  const newEnum: Api.EventTypesEnum = {
    enumName: "",
    enumValue: 0,
    comment: "",
    doorEventClassId: null,
    eventClassEnumName: "None"
  };
  return newEnum;
};

export const createEmptyEnum = (): Api.BaseEnumeration => {
  const newEnum: Api.BaseEnumeration = {
    enumName: "",
    enumValue: 0,
    comment: ""
  };
  return newEnum;
};

export const createEmptyMessageType = (): Api.MessageType => {
  const newMessageType: Api.MessageType = {
    enumName: "",
    enumValue: 0,
    comment: "",
    mappingNeeded: true,
    mapping: {
      eventClass: null,
      eventTypes: []
    }
  };
  return newMessageType;
};

export const createEmptyIoDatatype = (): Api.ioDataType => {
  const newEnum: Api.ioDataType = {
    enumName: "",
    value: 0,
    comment: ""
  };
  return newEnum;
};

export const createEmptyIoBasicDatatype = (): Api.IoBasicDataType => {
  const newEnum: Api.IoBasicDataType = {
    id: null,
    enumValue: 0,
    enumName: "",
    comment: "",
    iodataSections: []
  };
  return newEnum;
};

export const createEmptyIoDataSection = (): Api.IodataSections => {
  const newDataSection: Api.IodataSections = {
    name: "",
    mask: "",
    offset: 0,
    enumeration: null
  };
  return newDataSection;
};

export const createEmptyIoOptionType = (): Api.IoOptionsEnumType => {
  const newEnum: Api.IoOptionsEnumType = {
    enumID: 0,
    enumTypeName: ""
  };
  return newEnum;
};

export const createEmptyIoOption = (): Api.ioOption => {
  const newEnum: Api.ioOption = {
    enumName: "",
    value: 0,
    comment: "",
    enumTypeID: 1
  };
  return newEnum;
};

export const createEmptyDataActCode = (): Api.DataActCode | Api.EscalatorDataActCode => {
  const dataActCode: Api.DataActCode | Api.EscalatorDataActCode = {
    id: 0,
    name: "",
    code: 0
  };
  return dataActCode;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createEmptyIoBoardType = (pdApi: Api.AnyItem): Api.IoBoardType => {
  const newEnum: Api.IoBoardType = {
    enumName: "IO_BT_",
    boardTypeId: 0,
    comment: "",
    isMultiNet: false,
    installOrder: null
  };
  return newEnum;
};
export const createEmptyStandardNode = (parentBoardId: string): Api.StandardNode => {
  const newStandardNode = {
    parentBoardId: parentBoardId,
    cpu: createEmptyEnum(),
    network: createEmptyEnum(),
    standardPoints: []
  };
  return newStandardNode;
};
export const createEmptyStandardBoard = (pdApi: State.PdApi): Api.StandardBoard => {
  const newStandardBoard: Api.StandardBoard = {
    id: "",
    revision: "",
    locked: false,
    comment: "",
    boardType: createEmptyIoBoardType(pdApi),
    standardNodes: []
  };
  return newStandardBoard;
};
export const createEmptyIoBoard = (pdApi: State.PdApi, ioConfigurationId: string): Api.IoBoard => {
  const newStandardBoard: Api.IoBoard = {
    id: "",
    configId: ioConfigurationId,
    // modelId: "",
    parallelIdx: 0,
    side: 0,
    index: 0,
    installOrder: null,
    standard: createEmptyStandardBoard(pdApi),
    ionodes: []
  };
  return newStandardBoard;
};
export const createEmptyStandardPoint = (parentNodeId: string): Api.StandardPoint => {
  const newStandardPoint: Api.StandardPoint = {
    id: "",
    parentNodeId: parentNodeId,
    ioname: "",
    ioindex: 0,
    isInput: true,
    isConfigured: false,
    standardActions: []
  };
  return newStandardPoint;
};
export const createEmptyIonodePoint = (parentNodeId: string): Api.IoNodePoint => {
  const newIonodePoint: Api.IoNodePoint = {
    id: "",
    nodeId: parentNodeId,
    ioname: "",
    ioindex: 0,
    isInput: true,
    isConfigured: false,
    comment: null,
    ionodePointActions: []
  };
  return newIonodePoint;
};

export const createEmptyIoConfigurations = (pdApi: State.PdApi): Api.IoConfiguration => {
  const newIoConfiguration: Api.IoConfiguration = {
    friendlyName: "",
    comment: "",
    locked: false,
    kceProduct: _.values(pdApi.kceProducts)[0],
    kceVersion: _.values(pdApi.kceVersions)[0]
  };
  return newIoConfiguration;
};
export const createEmptyStandardAction = (parentPointId: string): Api.StandardAction => {
  const newStandardAction: Api.StandardAction = {
    parentPointId: parentPointId,
    data: 0,
    options: 0,
    holdOrWeight: 0,
    actionSide: 0,
    actionIndex: 0,
    actionDeck: 0,
    inputStuckSupervision: 0,
    inputFilterTime: 0,
    dataFormatNavigation: {
      id: "2841a5a3-f7bd-433e-885d-6599f035e452",
      iodataSections: null,
      enumName: "NONE",
      enumValue: 0,
      comment: "No data type"
    },
    eventClassEnumNavigation: createEmptyEnum(),
    eventTypeEnumNavigation: createEmptyEnum()
  };
  return newStandardAction;
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createEmptyDriveSignalGroup = (pdApi: State.PdApi): Api.DriveSignalGroup => {
  const newDriveSignalGroup = {
    id: 0,
    enumValue: 0,
    enumName: "",
    description: "",
    name: "",
    nameTranslationKey: "",
    locs: []
  };
  return newDriveSignalGroup;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createEmptyCommonEnumeration = (pdApi: State.PdApi): Api.CommonEnumeration => {
  const newEnumerations: Api.CommonEnumeration = {
    id: "",
    name: "",
    target: "",
    comment: "",
    isLocalized: true,
    enumerationItems: []
  };
  return newEnumerations;
};

export const createEmptyCommonEnumerationItem = (
  parentObjectId: string,
  pdApi: State.PdApi
): Api.CommonEnumerationItem => {
  const englishLocale = pdApi.locales.find(loc => loc.locale === "en-US");
  if (!englishLocale) {
    console.log("ERROR: Locale en-US is not found");
    const errorMsg = { message: "ERROR: Locale en-US is not found" };
    throw errorMsg;
  }
  const englishLocalization: Api.LocsEnumeration = {
    text: "",
    description: "",
    needsUpdate: true,
    localeNavigation: englishLocale
  };
  const emptyEnumerationItem = {
    enumerationId: parentObjectId,
    enumName: "",
    enumValue: 0,
    comment: "",
    englishName: "",
    description: "",
    locs: [englishLocalization]
  };
  return emptyEnumerationItem;
};

// create empty locale
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createEmptyLocale = (pdApi: State.PdApi): Api.Locale =>
  ({
    locale: "",
    friendlyName: "",
    added: new Date(Date.now()).toISOString(),
    boundLocale: "",
    comment: ""
  } as Api.Locale);

// Get options of fault priorities available for the domain group
export const getFaultPriorityOptionsByDomainId = (
  domainId: number,
  pdApi: State.PdApi
): DropdownOption[] => {
  const { domains, domainGroups, faultPriorities } = pdApi;

  // TODO: (MM) temporary hack, for faults in non-kone domaingroup, return lift domaingroup fault priorities
  const KCECPU_domainId = 1;
  const domainGroup = domainGroups[domains[domainId].domainGroup.id];
  const groupId =
    domainGroup.enumName !== "NonKone"
      ? domains[domainId].domainGroup.id
      : domains[KCECPU_domainId].domainGroup.id;

  let faultPrioritiesList = _.values(faultPriorities).filter(
    faultPriority => faultPriority.domainGroup.id === groupId
  );
  
  // Fallback: if no priorities found for this domain group (e.g., Monitoring, Network), use Lift group priorities
  if (faultPrioritiesList.length === 0) {
    const liftGroupId = domains[KCECPU_domainId].domainGroup.id;
    faultPrioritiesList = _.values(faultPriorities).filter(
      faultPriority => faultPriority.domainGroup.id === liftGroupId
    );
  }
  
  const priorityOptions: DropdownOption[] = faultPrioritiesList
    .sort((a, b) => a.enumValue - b.enumValue)
    .map(faultPriority => ({
      key: faultPriority.id,
      text: faultPriority.enumName + " [" + faultPriority.enumValue + "]",
      value: faultPriority.id
    }));
  return priorityOptions;
};

export const getDomainGroupNames = (pdApi: State.PdApi): DropdownOption[] => {
  const domainsList = _.values(pdApi.domainGroups);
  const domainsOptions = domainsList.map(domain => ({
    key: domain.id,
    text: domain.enumName,
    value: domain.id
  }));
  return domainsOptions;
};

// Domain group options keyed by enumValue. Used where the stored/returned
// domain group reference is the enumValue rather than the primary key id
// (e.g. Grand Norm, where the backend persists domainGroupId as enumValue).
export const getDomainGroupNamesByEnumValue = (pdApi: State.PdApi): DropdownOption[] => {
  const domainsList = _.values(pdApi.domainGroups);
  const domainsOptions = domainsList.map(domain => ({
    key: domain.enumValue,
    text: domain.enumName,
    value: domain.enumValue
  }));
  return domainsOptions;
};

// IO Config related get utils
export const getBoardTypes = (pdApi: State.PdApi): DropdownOption[] => {
  const boardTypesList = _.values(pdApi.ioBoardTypes);
  const boardTypeOptions = boardTypesList.map(type => ({
    key: type.boardTypeId,
    text: parseEnumStart(type.enumName, 2) + " [" + type.boardTypeId + "]",
    value: type.boardTypeId
  }));
  return boardTypeOptions;
};
/**
 option.enumTypeID = 1; // Input
 option.enumTypeID = 2; // Output
 option.enumTypeID = 3; // InputFilter
 */
export const getIoOptions = (
  ioOptions: Api.ioOption[],
  select: "input" | "output"
): DropdownOption[] => {
  const selector = select === "input" ? 1 : 2;
  const options = ioOptions
    .filter(option => option.enumTypeID == selector)
    .sort((a, b) => a.value - b.value)
    .map(item => ({
      key: item.value,
      text: item.enumName + " [" + item.value + "]",
      value: item.value
    }));
  return options;
};

export const getOnOffOptions = (): DropdownOption[] => {
  const options = [
    {
      key: 0,
      text: "OFF [0]",
      value: 0
    },
    {
      key: 1,
      text: "ON [1]",
      value: 1
    }
  ];
  return options;
};

export const getcommonEnumOptions = (
  enumerations: Api.CommonEnumeration[],
  enumerationName: string
): DropdownOption[] => {
  const escalatorEnumNames = ["E_paramFormat", "E_paramType", "E_additionalData1", "E_additionalData2",
    "E_faultType", "E_displayReset", "E_indicatorOutput", "E_statisticsCounter", "E_faultLogToCustomer", "E_krd"
  ];
  let commonEnums: Api.BaseEnumeration[] = [];
  if (enumerationName.includes("VectorBinding")) {
    commonEnums = _.filter(
      enumerations,
      item => item.name === enumerationName && item.target === "UI"
    )[0].enumerationItems;
  } else if (escalatorEnumNames.includes(enumerationName)) {
    commonEnums = _.filter(
      enumerations,
      item => item.name === enumerationName && item.target === "Escalator"
    )[0].enumerationItems;
  }
  const commonEnumsOptions = commonEnums
    .sort((a, b) => a.enumValue - b.enumValue)
    .map((item: Api.BaseEnumeration) => ({
      key: item.enumValue,
      text: item.enumName + " [" + item.enumValue + "]",
      value: item.enumValue
    }));
  return commonEnumsOptions;
};

export const getBaseEnumOptions = (option: Api.BaseEnumeration[]): DropdownOption[] => {
  const options = option
    .sort((a, b) => (a.enumName < b.enumName ? -1 : a.enumName > b.enumName ? 1 : 0))
    .map((item: Api.BaseEnumeration) => ({
      key: item.enumValue,
      text: item.enumName,
      value: item.enumValue
    }));
  return options;
};

export const getActionEnumOptions = (option: Api.BaseEnumeration[]): DropdownOption[] => {
  const options = option
    .sort((a, b) => a.enumValue - b.enumValue)
    .map((item: Api.BaseEnumeration) => ({
      key: item.enumValue,
      text: item.enumName + " [" + item.enumValue + "]",
      value: item.enumValue
    }));
  return options;
};

export const getRoleOptions = (option: Api.Role[]): DropdownOption[] => {
  const options = option.map((item: Api.Role) => ({
    key: item.id,
    text: item.name,
    value: item.id
  }));
  return options;
};
export const getCpuOptions = (option: Api.BaseEnumeration[]): DropdownOption[] => {
  const options = option
    .sort((a, b) => a.enumValue - b.enumValue)
    .map((item: Api.BaseEnumeration) => ({
      key: item.enumValue,
      text: parseEnumStart(item.enumName, 2) + " [" + item.enumValue + "]",
      value: item.enumValue
    }));
  return options;
};
export const getIoBasicDataTypeOptions = (option: Api.IoBasicDataType[]): DropdownOption[] => {
  const options = option
    .sort((a, b) => a.enumValue - b.enumValue)
    .map((item: Api.IoBasicDataType) => ({
      key: item.enumValue,
      text: item.enumName + " [" + item.enumValue + "]",
      value: item.id
    }));
  return options;
};

export const getEnumerationItemOptions = (
  option: Api.IodataSectionsEnumerationItem[]
): DropdownOption[] => {
  const options = option
    .sort((a, b) => a.enumValue - b.enumValue)
    .map((item: Api.IodataSectionsEnumerationItem) => ({
      key: item.enumValue,
      text: item.enumName + " [" + item.enumValue + "]",
      value: item.enumValue
    }));
  return options;
};

export const setDriveSignalExtraProperties = (
  driveSignal: Api.DriveSignal,
  pdApi: State.PdApi
): Api.DriveSignal => {
  const { signalGroups } = pdApi;
  const signalGroup = signalGroups.find(i => i.enumValue === driveSignal.signalGroupEnumValue);
  const signalGroupEnumName = signalGroup
    ? signalGroup.enumName + " [" + signalGroup.enumValue + "]"
    : "";
  return {
    ...driveSignal,
    signalGroupEnumName: signalGroupEnumName
  };
};

export const setEscalatorRTDExtraProperties = (
  escalatorRTD: Api.EscalatorRTD,
  pdApi: State.PdApi
): Api.EscalatorRTD => {
  const { escalatorRTDGroups } = pdApi;
  const escalatorRTDGroup = escalatorRTDGroups.find(
    i => i.enumValue === escalatorRTD.rtdGroupEnumValue
  );
  const escalatorRTDGroupEnumName = escalatorRTDGroup
    ? escalatorRTDGroup.enumName + " [" + escalatorRTDGroup.enumValue + "]"
    : "";
  return {
    ...escalatorRTD,
    rtdGroupEnumName: escalatorRTDGroupEnumName
  };
};

export const getEscalatorRTDGroupNames = (pdApi: State.PdApi): DropdownOption[] => {
  const escalatorRTDGroupsList = _.values(pdApi.escalatorRTDGroups);
  const escalatorRTDGroupsOptions = escalatorRTDGroupsList
    .sort((a, b) => a.enumValue - b.enumValue)
    .map(escalatorRTDGroup => ({
      key: escalatorRTDGroup.id,
      text: escalatorRTDGroup.enumName + " [" + escalatorRTDGroup.enumValue + "]",
      value: escalatorRTDGroup.enumValue
    }));
  return escalatorRTDGroupsOptions;
};

export const setEscalatorEventLogExtraProperties = (
  escalatorEventLog: Api.EscalatorEventLog,
  pdApi: State.PdApi
): Api.EscalatorEventLog => {
  const { enumerations } = pdApi;
  
  const escalatorData1 =
    getcommonEnumOptions(enumerations, "E_additionalData1").find(
      item => item.key === (escalatorEventLog as Api.EscalatorEventLog).data1EnumValue
    )?.text?.replace(` [${(escalatorEventLog as Api.EscalatorEventLog).data1EnumValue}]`, "").trim() || "";
  const escalatorData1EnumName = escalatorData1
    ? escalatorData1 + " [" + (escalatorEventLog as Api.EscalatorEventLog).data1EnumValue + "]"
    : "";

  const escalatorData2 =
    getcommonEnumOptions(enumerations, "E_additionalData2").find(
      item => item.key === (escalatorEventLog as Api.EscalatorEventLog).data2EnumValue
    )?.text?.replace(` [${(escalatorEventLog as Api.EscalatorEventLog).data2EnumValue}]`, "").trim() || "";
  const escalatorData2EnumName = escalatorData2
      ? escalatorData2 + " [" + (escalatorEventLog as Api.EscalatorEventLog).data2EnumValue + "]"
      : "";

  return {
    ...escalatorEventLog,
    data1EnumName: escalatorData1EnumName,
    data2EnumName: escalatorData2EnumName
  };
};

export const setEventTypeExtraProperties = (
  eventType: Api.EventTypesEnum,
  pdApi: State.PdApi
): Api.EventTypesEnum => {
  const { doorEventClasses } = pdApi;
  if (eventType.doorEventClassId !== null) {
    const eventClass = doorEventClasses.find(i => i.enumValue === eventType.doorEventClassId);
    const eventClassEnumMatch = eventClass
      ? eventClass.enumName + " [" + eventClass.enumValue + "]"
      : "";
    return {
      ...eventType,
      eventClassEnumName: eventClassEnumMatch
    };
  } else {
    return {
      ...eventType,
      eventClassEnumName: "None"
    };
  }
};

export const getDriveSignalGroupNames = (pdApi: State.PdApi): DropdownOption[] => {
  const signalGroupsList = _.values(pdApi.signalGroups);
  const signalGroupsOptions = signalGroupsList
    .sort((a, b) => a.enumValue - b.enumValue)
    .map(driveSignalGroup => ({
      key: driveSignalGroup.id,
      text: driveSignalGroup.enumName + " [" + driveSignalGroup.enumValue + "]",
      value: driveSignalGroup.enumValue
    }));
  return signalGroupsOptions;
};

export const getDomainOptionsByDomainGroup = (
  pdApi: State.PdApi,
  domainGroupName: Api.GroupName
): DropdownOption[] => {
  let filteredDomains: Api.Domain[] = [];
  const domainGroupId = _.values(pdApi.domainGroups).find(
    domainGroup => domainGroup.enumName === domainGroupName
  )?.id;
  const domains = _.values(pdApi.domains);
  filteredDomains = domains.filter(domain => domain.domainGroup.id === domainGroupId);

  const filteredDomainoptions = filteredDomains
    .sort((a, b) => a.enumValue - b.enumValue)
    .map(domain => ({
      key: domain.id,
      text: domain.enumName + " [" + domain.enumValue + "]",
      value: domain.id
    }));
  return filteredDomainoptions;
};
export const getDomainOptionsByDomainId = (
  domainId: number,
  separateAPM: boolean,
  pdApi: State.PdApi
): DropdownOption[] => {
  const { domains } = pdApi;
  const domain: Api.Domain = domains[domainId];
  const groupId = domain.domainGroup.id;

  const domainsList = _.values(pdApi.domains).filter(domain => domain.domainGroup.id === groupId);
  /*if (separateAPM) {
    if (domain.enumValue === 1000) {
      domainsList = [domain];
    } else {
      domainsList = domainsList.filter(domain => domain.enumValue !== 1000);
    }
  }*/
  const domainOptions = domainsList
    .sort((a, b) => a.enumValue - b.enumValue)
    .map(domain => ({
      key: domain.id,
      text: domain.enumName + " [" + domain.enumValue + "]",
      value: domain.id
    }));
  return domainOptions;
};

export const getDomainOptionsByDomainGroupId = (
  domainGroupId: number,
  separateAPM: boolean,
  pdApi: State.PdApi
): DropdownOption[] => {
  const domainsList = _.values(pdApi.domains).filter(
    domain => domain.domainGroup.id === domainGroupId
  );

  const domainOptions = domainsList
    .sort((a, b) => a.enumValue - b.enumValue)
    .map(domain => ({
      key: domain.id,
      text: domain.enumName + " [" + domain.enumValue + "]",
      value: domain.id
    }));
  return domainOptions;
};

export const getIoEnumOptions = (enumerations: Api.CommonEnumeration[]): DropdownOption[] => {
  const onlyIO = _.filter(enumerations, item => item.target === "I/O");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const enumOptions = onlyIO.map((i, index) => ({
    key: i.id,
    text: i.name,
    value: i.id
  }));
  return enumOptions;
};

export const getIoDataSectionsOptions = (
  iodataSections: Api.IodataSections[]
): DropdownOption[] => {
  const options = iodataSections.map((i, index) => ({
    key: index,
    text: i.name,
    value: i.id
  }));
  return options;
};

export const getInputFilterOptions = (ioOptions: Api.ioOption[]): DropdownOption[] => {
  const options = ioOptions
    .filter(option => option.enumTypeID == 3)
    .sort((a, b) => a.value - b.value)
    .map(item => ({
      key: item.value,
      text: item.enumName + " [" + item.value + "]",
      value: item.value
    }));
  return options;
};

// Get options of fault categories
export const getFaultCategoryOptions = (faultCategories: Api.FaultCategories): DropdownOption[] => {
  const faultCategoryList = _.values(faultCategories);
  const faultCategoryOptions = faultCategoryList.map(category => ({
    key: category.id,
    text: category.enumName,
    value: category.id
  }));
  return faultCategoryOptions;
};

export const getCategoryPath = (
  parameterCatecory: Api.ParameterCategory,
  parameterCategories: Api.ParameterCategory[],
  useEnumNames = true
): string => {
  if (!parameterCatecory) {
    return "";
  }
  let path: string = useEnumNames ? parameterCatecory.enumName : parameterCatecory.name;
  let parentCategoryRef: Api.Reference | null = parameterCatecory.parentCategory;
  while (parentCategoryRef) {
    const parentCategoryId = parentCategoryRef.id;
    const parentCategory = parameterCategories.find(category => category.id === parentCategoryId);
    if (!parentCategory) {
      return `ERROR: NOT FOUND / ${path}`;
    }
    path = `${useEnumNames ? parentCategory.enumName : parentCategory.name} / ${path}`;
    parentCategoryRef = parentCategory.parentCategory;
  }
  return path;
};

export const getCategoryPathForParameter = (
  parameter: Api.AnyParameter,
  parameterCategories: Api.ParameterCategory[],
  useEnumNames = true
): string => {
  if (!parameter.parameterCategory) {
    return "";
  }
  const category = parameterCategories.find(
    category => category.id === parameter.parameterCategory?.id
  );
  if (!category) {
    return "ERROR: NOT FOUND";
  }
  return getCategoryPath(category, parameterCategories, useEnumNames);
};

// Get options of parameter categories
export const getParameterCategoryOptions = (
  domainId: number,
  pdApi: State.PdApi
): DropdownOption[] => {
  const { domains, parameterCategories, domainGroups } = pdApi;
  const groupId = domains[domainId].domainGroup.id;
  const domainGroup: Api.DomainGroup = domainGroups[groupId];
  const parameterCategoryList = _.values(parameterCategories).filter(
    category => category.domainGroup.id === groupId
  );
  const parameterCategoryOptions = parameterCategoryList
    .map(category => ({
      key: category.id,
      text: getCategoryPath(category, parameterCategoryList),
      value: category.id
    }))
    .concat(
      domainGroup.enumName.toUpperCase() !== "SAFETY"
        ? []
        : [
            {
              key: 0,
              text: "",
              value: 0
            }
          ]
    );
  return parameterCategoryOptions;
};

export const getFeatureOptions = (features: Api.Features): DropdownOption[] => {
  const featuresValues = _.values(features);
  return featuresValues.map(feature => ({
    key: feature.id,
    text: feature.swName,
    value: feature.id
  }));
};

//Get option for UserGroup
export const getUsergroups = (userGroups: Api.UserGroup[]): DropdownOption[] => {
  const allOption = [
    {
      key: -1,
      text: "All",
      value: -1
    }
  ];
  const mappedOptions = _.values(userGroups).map(userGroup => ({
    key: userGroup.id,
    text: userGroup.enumName,
    value: userGroup.id
  }));
  const userListOptions = [...allOption, ...mappedOptions];
  return _.sortBy(userListOptions, ["text"]);
};

// Get options of Kce versions
export const getKceVersionOptions = (kceVersions: Api.KceVersions): DropdownOption[] => {
  const kceVersionList = _.values(kceVersions);
  const allOption = [
    {
      key: -1,
      text: "All",
      value: -1
    }
  ];
  const mappedOptions = kceVersionList.map(kceVersion => ({
    key: kceVersion.id,
    text: kceVersion.name,
    value: kceVersion.id
  }));
  const kceVersionOptions = [...allOption, ...mappedOptions];
  return _.sortBy(kceVersionOptions, ["text"]);
};
// eslint-disable-next-line
export const getVersionOptions = (versions: any, domains: Api.Domains): DropdownOption[] => {
  const VersionList = _.values(versions);
  const allOption = [
    {
      key: -1,
      text: "All",
      value: -1
    }
  ];
  const mappedOptions = VersionList.map(version => ({
    key: version.id,
    text: `${version.number}.${domains[version.domain.id].enumName}`,
    value: version.id
  }));
  const kceVersionOptions = [...allOption, ...mappedOptions];
  return kceVersionOptions;
};

// Get options of Drive Versions
export const getDriveVersionOptions = (
  domains: Api.Domains,
  driveVersions: Api.DriveVersions
): DropdownOption[] => {
  const driveVersionList = _.values(driveVersions);
  const allOption = [
    {
      key: -1,
      text: "All",
      value: -1
    }
  ];
  const mappedOptions = driveVersionList.map(driveVersion => ({
    key: driveVersion.id,
    text: `${driveVersion.year}.${driveVersion.week}.${domains[driveVersion.domain.id].enumName}`,
    value: driveVersion.id
  }));

  const driveVersionOptions = [...allOption, ...mappedOptions];
  return _.sortBy(driveVersionOptions, ["text"]).reverse();
};

// Get options of Escalator Versions
export const getEscalatorVersionOptions = (
  domains: Api.Domains,
  escalatorVersions: Api.EscalatorVersions
): DropdownOption[] => {
  const escalatorVersionList = _.values(escalatorVersions);
  const allOption = [
    {
      key: -1,
      text: "All",
      value: -1
    }
  ];
  const mappedOptions = escalatorVersionList.map(escalatorVersion => ({
    key: escalatorVersion.id,
    text: `${escalatorVersion.number}.${domains[escalatorVersion.domain.id].enumName}`,
    value: escalatorVersion.id
  }));

  const escalatorVersionOptions = [...allOption, ...mappedOptions];
  return _.sortBy(escalatorVersionOptions, ["text"]).reverse();
};

//Get Escalator domain options based of parameters available in the domain
export const getEscalatorParamFaultDomainOptions = (
  domains: Api.Domains,
  escalatorParameters: Api.EscalatorParameter[],
  escalatorFaults: Api.EscalatorFault[]
): DropdownOption[] => {
  const usedDomainIds = _.uniq([...escalatorParameters.map(parameter => parameter.domain.id), ...escalatorFaults.map(fault => fault.domain.id)]);
  const allOption = [
    {
      key: -1,
      text: "Select",
      value: -1
    }
  ];
  const usedDomainOptions = usedDomainIds
    .sort((a, b) => domains[a].enumValue - domains[b].enumValue)
    .map(usedDomainId => ({
      key: usedDomainId,
      text: domains[usedDomainId].enumName + " [" + domains[usedDomainId].enumValue + "]",
      value: usedDomainId
    }));
  return [...allOption, ...usedDomainOptions];
};

// Get options of Kce products
export const getKceProductOptions = (kceProducts: Api.KceProducts): DropdownOption[] => {
  const kceProductList = _.values(kceProducts);
  const allOption = [
    {
      key: -1,
      text: "All",
      value: -1
    }
  ];
  const mappedOptions = kceProductList.map(kceProduct => ({
    key: kceProduct.id,
    text: kceProduct.productName,
    value: kceProduct.id
  }));
  const kceProductOptions = [...allOption, ...mappedOptions];
  return _.sortBy(kceProductOptions, ["text"]);
};

// Get options of domain groups
export const getDomainGroupOptions = (domainGroups: Api.DomainGroups): DropdownOption[] => {
  const domainGroupValues = _.values(domainGroups);
  const mappedOptions = _.sortBy(
    domainGroupValues.map(
      domainGroup =>
        ({
          key: domainGroup.enumValue,
          text: domainGroup.enumName,
          value: domainGroup.enumValue
        } as DropdownOption)
    ),
    ["text"]
  );
  const allOption = [
    {
      key: -1,
      text: "All",
      value: -1
    }
  ];
  const domainGroupOptions = [...allOption, ...mappedOptions];
  return domainGroupOptions;
};

// Get options of domains
export const getDomainOptions = (
  domains: Api.Domains,
  selectedDomainGroupId?: number
): DropdownOption[] => {
  const domainList = _.values(domains).filter(domain =>
    //Todo i have clean up this code
    selectedDomainGroupId ? domain.domainGroup.id === selectedDomainGroupId : true
  );
  const mappedOptions = _.sortBy(
    domainList.map(domain => ({
      key: domain.enumValue,
      text: domain.enumName,
      value: domain.enumValue
    })),
    ["text"]
  );
  const allOption = [
    {
      key: -1,
      text: "All",
      value: -1
    }
  ];
  const domainOptions = [...allOption, ...mappedOptions];
  return domainOptions;
};

export const getDataActOptions = (dataActs: Api.DataActCode[] | Api.EscalatorDataActCode[]): DropdownOption[] => {
  const dataActList = dataActs.map((dataAct: Api.DataActCode | Api.EscalatorDataActCode) => ({
    key: dataAct.id,
    text: dataAct.name + " [" + dataAct.code + "]",
    value: dataAct.id
  }));

  return dataActList;
};

export const getGrandNormOptions = (grandNorms: Api.GrandNorm[]): DropdownOption[] => {
  const grandNormList = grandNorms.map((grandNorm: Api.GrandNorm) => ({
    key: grandNorm.id,
    text: grandNorm.name + " [" + grandNorm.code + "]",
    value: grandNorm.id
  }));

  return grandNormList;
};

// Get options of item types, keep itemTypeList in sync with the backend!
export const getItemTypeOptions = (selectedDomainGroupEnumValue?: number): DropdownOption[] => {
  const itemTypeList = [
    { id: 1, name: "Lift Parameters", belongsToDomainGroup: 1 },
    { id: 2, name: "Lift Faults", belongsToDomainGroup: 1 },
    { id: 3, name: "Lift Fault Components", belongsToDomainGroup: 1 },
    { id: 4, name: "Lift Indicators", belongsToDomainGroup: 1 },
    { id: 5, name: "APM Faults", belongsToDomainGroup: 9 },
    { id: 6, name: "Known Elevator Modes", belongsToDomainGroup: 1 },
    { id: 7, name: "Start Sequence", belongsToDomainGroup: 1 },
    { id: 8, name: "Door Indicators", belongsToDomainGroup: 1 },
    { id: 9, name: "Safety Indicators", belongsToDomainGroup: 1 },
    { id: 10, name: "Indicator Groups", belongsToDomainGroup: 1 },
    { id: 11, name: "Lift Parameter Categories", belongsToDomainGroup: 1 },
    { id: 12, name: "Drive Parameters", belongsToDomainGroup: 2 },
    { id: 13, name: "Drive Faults", belongsToDomainGroup: 2 },
    { id: 14, name: "Drive Signals", belongsToDomainGroup: 2 },
    { id: 15, name: "Drive Tests", belongsToDomainGroup: 2 },
    { id: 16, name: "Drive Signal Groups", belongsToDomainGroup: 2 },
    { id: 17, name: "Drive Parameter Categories", belongsToDomainGroup: 2 },
    { id: 18, name: "Group Faults", belongsToDomainGroup: 3 },
    { id: 19, name: "Group Parameters", belongsToDomainGroup: 3 },
    { id: 20, name: "Group Parameter Categories", belongsToDomainGroup: 3 },
    { id: 21, name: "Safety Parameters", belongsToDomainGroup: 4 },
    { id: 22, name: "Escalator Parameters", belongsToDomainGroup: 7 },
    { id: 23, name: "Door Parameters", belongsToDomainGroup: 8 },
    { id: 24, name: "APM Parameters", belongsToDomainGroup: 9 },
    { id: 25, name: "APM Parameter Categories", belongsToDomainGroup: 9 },
    { id: 26, name: "Door Faults", belongsToDomainGroup: 8 },
    { id: 27, name: "Escalator Faults", belongsToDomainGroup: 7 },
    { id: 28, name: "Signalization Faults", belongsToDomainGroup: 11 },
    { id: 29, name: "Network Faults", belongsToDomainGroup: 10 },
    { id: 30, name: "Monitoring Faults", belongsToDomainGroup: 5 },
    { id: 31, name: "Monitoring Fault Categories", belongsToDomainGroup: 5 },    
    { id: 32, name: "Escalator RTDs", belongsToDomainGroup: 7 },    
    { id: 33, name: "Escalator RTD Groups", belongsToDomainGroup: 7 },
    { id: 34, name: "Escalator Event logs", belongsToDomainGroup: 7 }
  ];
  const allOption = [
    {
      key: 0,
      text: "All",
      value: 0
    }
  ];
  const mappedOptions = _.sortBy(
    itemTypeList
      .filter(itemType =>
        selectedDomainGroupEnumValue !== -1
          ? itemType.belongsToDomainGroup === selectedDomainGroupEnumValue
          : true
      )
      .map(itemType => ({
        key: itemType.id,
        text: itemType.name,
        value: itemType.id
      })),
    ["text"]
  );
  const itemTypeOptions = [...allOption, ...mappedOptions];
  return itemTypeOptions;
};

export const setDriveVersionExtraProperties = (
  driveVersion: Api.DriveVersion,
  pdApi: State.PdApi
): Api.DriveVersion => {
  const { domains } = pdApi;
  const domainName = (domains[driveVersion.domain.id].enumName +
    " [" +
    domains[driveVersion.domain.id].enumValue +
    "]") as string;
  return {
    ...driveVersion,
    domainName: domainName
  };
};

export const setEscalatorVersionExtraProperties = (
  escalatorVersion: Api.EscalatorVersion,
  pdApi: State.PdApi
): Api.EscalatorVersion => {
  const { domains } = pdApi;
  const domainName = (domains[escalatorVersion.domain.id].enumName +
    " [" +
    domains[escalatorVersion.domain.id].enumValue +
    "]") as string;
  return {
    ...escalatorVersion,
    domainName: domainName
  };
};

export const setDoorVersionExtraProperties = (
  doorVersion: Api.DoorVersion,
  pdApi: State.PdApi
): Api.DoorVersion => {
  const { domains } = pdApi;
  const domainName = (domains[doorVersion.domain.id].enumName +
    " [" +
    domains[doorVersion.domain.id].enumValue +
    "]") as string;
  return {
    ...doorVersion,
    domainName: domainName
  };
};

export const setDomainEnumExtraProperties = (
  domain: Api.Domain,
  pdApi: State.PdApi
): Api.Domain => {
  const { domainGroups } = pdApi;
  const groupName = domainGroups[domain.domainGroup.id].enumName as string;
  return {
    ...domain,
    extraDomainGroupName: groupName
  };
};
export const setGrandNormExtraProperties = (
  grandNorm: Api.GrandNorm,
  pdApi: State.PdApi
): Api.GrandNorm & { extraDomainGroupName?: string } => {
  const { domainGroups } = pdApi;
  // The backend stores/returns domainGroupId as the domain group's enumValue
  // (not the primary key id), so resolve the name by matching enumValue.
  const matchedGroup = _.values(domainGroups).find(
    domainGroup => domainGroup.enumValue === grandNorm.domainGroupId
  );
  const groupName = matchedGroup ? (matchedGroup.enumName as string) : "";
  return {
    ...grandNorm,
    extraDomainGroupName: groupName
  };
};

export const setParameterCategoryEnumExtraProperties = (
  parameterCategory: Api.ParameterCategory,
  pdApi: State.PdApi
): Api.ParameterCategory => {
  const { domainGroups, domains } = pdApi;
  const groupName = domainGroups[parameterCategory.domainGroup.id].enumName as string;
  const domainName = parameterCategory.domain && domains[parameterCategory.domain.id]
    ? domains[parameterCategory.domain.id].enumName + " [" + domains[parameterCategory.domain.id].enumValue + "]"
    : "";
  return {
    ...parameterCategory,
    extraDomainGroupName: groupName,
    extraDomainName: domainName
  };
};

// Add extra properties to the fault component, which contains more understandable format for domain properties
export const setFaultComponentExtraProperties = (
  faultComponent: Api.FaultComponentEnumeration,
  pdApi: State.PdApi
): Api.FaultComponentEnumeration => {
  const { domainGroups } = pdApi;
  const domainGroupId = faultComponent.domainGroup?.id;
  const groupName =
    domainGroupId && domainGroups[domainGroupId]
      ? (domainGroups[domainGroupId].enumName as string)
      : "Unknown";
  return {
    ...faultComponent,
    extraDomainGroupName: groupName
  };
};

// Add extra properties to the fault, which contains more understandable format for some properties
export const setFaultExtraProperties = (fault: Api.AnyFault, pdApi: State.PdApi): Api.AnyFault => {
  const { domains, domainGroups, faultPriorities, faultCategories, monitoringFaultCategories, dataActCodes,escalatorDataActCodes, grandNorms } = pdApi;
  const groupId = domains[fault.domain.id].domainGroup.id;
  const domainName =
    domains[fault.domain.id].enumName + " [" + domains[fault.domain.id].enumValue + "]";
  const domainGroupName = domainGroups[groupId].enumName;
  const priorityName =
    faultPriorities[fault.priority.id].enumName +
    " [" +
    faultPriorities[fault.priority.id].enumValue +
    "]";
  let modifiedFault: Api.AnyFault = {
    ...fault,
    commonExtra: {
      domainGroupName,
      domainName,
      priorityName
    },
    extra: {}
  };
  if (domainGroupName === "Drive") {
    const faultCategoryId = (fault as Api.DriveFault).faultCategory.id;
    modifiedFault = {
      ...modifiedFault,
      extra: {
        faultCategoryName: faultCategories[faultCategoryId].enumName
      }
    };
  }
  if (domainGroupName === "Monitoring" && monitoringFaultCategories) {
    const faultCategoryId = (fault as Api.MonitoringFault).faultCategory.id;
    const index = monitoringFaultCategories.findIndex(category => category.id === faultCategoryId);
    if (faultCategoryId && monitoringFaultCategories[index]) {
      modifiedFault = {
        ...modifiedFault,
        extra: {
          faultCategoryName: monitoringFaultCategories[index].enumName
        }
      };
    }
  }
  if ((domainGroupName === "Lift" || domainGroupName === "APM") && dataActCodes) {
    if ("dataActId" in fault) {
      const dataActIndex = dataActCodes.findIndex(
        code => code.id === (fault as Api.LiftFault).dataActId
      );
      const dataActName = dataActIndex >= 0 ? dataActCodes[dataActIndex].name : "";
      modifiedFault = {
        ...modifiedFault,
        extra: {
          ...(modifiedFault as Api.AnyItem).extra,
          dataActName: dataActName
        }
      };
    }
  }
  if (domainGroupName === "Lift" && grandNorms) {
    if ("grandNormId" in fault) {
      const grandNormIndex = grandNorms.findIndex(
        grandNorm => grandNorm.id === (fault as Api.LiftFault).grandNormId
      );
      const grandNormName = grandNormIndex >= 0 ? grandNorms[grandNormIndex].name : "";
      modifiedFault = {
        ...modifiedFault,
        extra: {
          ...(modifiedFault as Api.AnyItem).extra,
          grandNormName: grandNormName
        }
      };
    }
  }
  if ((domainGroupName === "Escalator") && escalatorDataActCodes) {
    if ("dataActId" in fault) {
      const dataActIndex = escalatorDataActCodes.findIndex(
        code => code.id === (fault as Api.EscalatorFault).dataActId
      );
      const dataActName = dataActIndex >= 0 ? escalatorDataActCodes[dataActIndex].name : "";
      modifiedFault = {
        ...modifiedFault,
        extra: {
          dataActName: dataActName
        }
      };
    }
  }
  return modifiedFault;
};

// Add extra properties to the parameter, which contains more understandable format for some properties
export const setParameterExtraProperties = (
  parameter: Api.AnyParameter,
  pdApi: State.PdApi
): Api.AnyParameter => {
  const { domains, domainGroups, parameterCategories, enumerations } = pdApi;
  const groupId = domains[parameter.domain.id].domainGroup.id;
  const domainName =
    domains[parameter.domain.id].enumName + " [" + domains[parameter.domain.id].enumValue + "]";
  const domainGroupName = domainGroups[groupId].enumName;
  let categoryName = "";
  let categoryPath = "";
  let escalatorParamTypeEnum = "";
  let escalatorParamFormatEnum = "";
  let visibilityText = "";

  if (domainGroupName === "Escalator") {
    escalatorParamTypeEnum =
      getcommonEnumOptions(enumerations, "E_paramType").find(
        item => item.key === (parameter as Api.EscalatorParameter).paramType
      )?.text || "";
    escalatorParamFormatEnum =
      getcommonEnumOptions(enumerations, "E_paramFormat").find(
        item => item.key === (parameter as Api.EscalatorParameter).paramFormat
      )?.text || "";
  }
  if (parameter.parameterCategory != null) {
    categoryName = parameterCategories[parameter.parameterCategory.id].enumName;
    const parameterCategoryList = _.values(parameterCategories).filter(
      category => category.domainGroup.id === groupId
    );
    categoryPath = getCategoryPathForParameter(parameter, parameterCategoryList);
  }

  if (domainGroupName === "Drive") {
    const visibilityIndex = visibilityOptions.findIndex(
      item => item.key === (parameter as Api.DriveParameter).visibility
    );
    visibilityText = visibilityOptions[visibilityIndex]
      ? visibilityOptions[visibilityIndex].text
      : "";
  }
  const isSafetyGroup = domainGroupName === "Safety";
  const dataTypeName = isSafetyGroup
    ? safetyDatatypeOptions.find(opt => opt.value === (parameter as Api.SafetyParameter).dataType)?.text ??
      String((parameter as Api.SafetyParameter).dataType)
    : undefined;

  const modifiedParameter = {
    ...parameter,
    commonExtra: {
      domainGroupName,
      domainName,
      categoryName,
      categoryPath
    },
    ...(domainGroupName === "Escalator" && { escalatorParamTypeEnum, escalatorParamFormatEnum }),
    ...(domainGroupName === "Drive" && { visibilityText }),
    ...(isSafetyGroup && { dataTypeName })
  };
  return modifiedParameter;
};

export const setGroupAPMParameterExtraProperties = (
  parameter: Api.GroupParameter | Api.ApmParamters,
  pdApi: State.PdApi
): Api.AnyParameter => {
  const { domains, domainGroups, parameterCategories } = pdApi;
  const groupId = domains[parameter.domain.id].domainGroup.id;
  const domainName =
    domains[parameter.domain.id].enumName + " [" + domains[parameter.domain.id].enumValue + "]";
  const domainGroupName = domainGroups[groupId].enumName;
  let categoryName = "";
  let categoryPath = "";
  if (parameter.parameterCategory != null) {
    categoryName = parameterCategories[parameter.parameterCategory.id].name;
    const parameterCategoryList = _.values(parameterCategories).filter(
      category => category.domainGroup.id === groupId
    );
    categoryPath = getCategoryPathForParameter(parameter, parameterCategoryList, false);
  }
  const feature = pdApi.features[parameter.featureDbId].swName;
  const modifiedParameter = {
    ...parameter,
    feature,
    commonExtra: {
      domainGroupName,
      domainName,
      categoryName,
      categoryPath
    }
  };
  return modifiedParameter;
};

// Decode raw value of value data format to the internal type
// Conversion copied partly from
// https://code.pot.cybercom.com/projects/KCEPCUI/repos/kce-tools-2/browse/mock_backend/mock-api.js
// and old (silverlight) KCETools
export const decodeValueDataFormat = (value: number): ValueDataFormatType => {
  // tslint:disable-next-line:no-bitwise
  const range = (value & 0x80) >>> 7; // 8. bit is range
  const valueDataFormat: ValueDataFormatType = {
    range: range === 0 ? "Number" : "Special",
    value: value
  };
  if (valueDataFormat.range === "Number") {
    // tslint:disable-next-line:no-bitwise
    const mode = (value & 0x40) >>> 6; // 7. bit is mode
    valueDataFormat.mode = mode === 0 ? "Editable" : "Display only";
    // tslint:disable-next-line:no-bitwise
    const type = (value & 0x20) >>> 5; // 6. bit is type
    valueDataFormat.type = type === 0 ? "Signed" : "Unsigned";
    // tslint:disable-next-line:no-bitwise
    const digits = (value & 0x1c) >>> 2; // 3-5 bits are digits,  0 = max, 1-7 = Number of digits
    valueDataFormat.digits = digits;
    // tslint:disable-next-line:no-bitwise
    const decimals = value & 0x03; // 1-3 bits are decimals, 0 = no decimals, 1-3 number of decimals
    valueDataFormat.decimals = decimals;
    // tslint:disable-next-line:no-bitwise
  }
  return valueDataFormat;
};

// Encode raw value of value data format from the internal type
export const encodeValueDataFormat = (valueDataFormat: ValueDataFormatType): number => {
  let value;
  if (valueDataFormat.range === "Special") {
    // tslint:disable-next-line:no-bitwise
    value = 0x80 | valueDataFormat.value;
  } else {
    value = 0;
    if (valueDataFormat.mode === "Display only") {
      // tslint:disable-next-line:no-bitwise
      value = value | (0x01 << 6);
    }
    if (valueDataFormat.type === "Unsigned") {
      // tslint:disable-next-line:no-bitwise
      value = value | (0x01 << 5);
    }
    if (valueDataFormat.digits) {
      // tslint:disable-next-line:no-bitwise
      value = value | (valueDataFormat.digits << 2);
    }
    if (valueDataFormat.decimals) {
      // tslint:disable-next-line:no-bitwise
      value = value | valueDataFormat.decimals;
    }
  }
  return value;
};

// Converts value data format into KONE string
export const getKoneFormatString = (valueDataFormat: ValueDataFormatType): string => {
  let result = "FMT_";
  const { range, mode, type, digits, decimals } = valueDataFormat;

  if (range === "Special") {
    return `${result}E${valueDataFormat.value - 128}`;
  } else {
    result += mode === "Display only" ? "D" : "E";
    result += type === "Unsigned" ? "U" : "S";
    return `${result}${digits}${decimals}`;
  }
};

// Check compared lift bindings are equal
export const isEqualBinding = (a: Api.LiftParameterBinding, b: Api.LiftParameterBinding): boolean =>
  a.kceProduct.id === b.kceProduct.id && a.kceVersion.id === b.kceVersion.id;

// Get an array of indexes of duplicated or invalid lift parameter bindings
export const getDuplicateLiftParameterBindings = (
  bindings: Api.LiftParameterBinding[]
): number[] => {
  const duplicateBindings: number[] = [];

  bindings?.forEach((binding, index) => {
    const firstIndex = _.findIndex(bindings, bindingToCompare =>
      isEqualBinding(bindingToCompare, binding)
    );
    const lastIndex = _.findLastIndex(bindings, bindingToCompare =>
      isEqualBinding(bindingToCompare, binding)
    );
    if (binding.kceVersion.id === -1 || binding.kceProduct.id === -1) {
      duplicateBindings.push(index);
    }
    if (firstIndex !== index) {
      duplicateBindings.push(firstIndex);
      duplicateBindings.push(index);
    }
    if (lastIndex !== index) {
      duplicateBindings.push(lastIndex);
      duplicateBindings.push(index);
    }
  });
  return _.uniq(duplicateBindings);
};

// Get an array of indexes of duplicated or invalid drive parameter bindings
export const getDuplicateDriveParameterBindings = (
  bindings: Api.DriveParameterBinding[]
): number[] => {
  const duplicateBindings: number[] = [];

  bindings.forEach((binding, index) => {
    const firstIndex = _.findIndex(
      bindings,
      bindingToCompare => bindingToCompare.driveVersion.id === binding.driveVersion.id
    );
    const lastIndex = _.findLastIndex(
      bindings,
      bindingToCompare => bindingToCompare.driveVersion.id === binding.driveVersion.id
    );
    if (binding.driveVersion.id === -1) {
      duplicateBindings.push(index);
    }
    if (firstIndex !== index) {
      duplicateBindings.push(firstIndex);
      duplicateBindings.push(index);
    }
    if (lastIndex !== index) {
      duplicateBindings.push(lastIndex);
      duplicateBindings.push(index);
    }
  });
  return _.uniq(duplicateBindings);
};

//gives duplicates or invalid escalator parameter bindings
export const getDuplicateEscalatorParameterBindings = (
  bindings: Api.EscalatorParameterBinding[],
  escalatorVersions: Api.EscalatorVersions,
  domainId: number
): number[] => {
  const duplicateBindings: number[] = [];

  bindings.forEach((binding, index) => {
    const firstIndex = _.findIndex(
      bindings,
      bindingToCompare => bindingToCompare.escalatorVersion.id === binding.escalatorVersion.id
    );
    const lastIndex = _.findLastIndex(
      bindings,
      bindingToCompare => bindingToCompare.escalatorVersion.id === binding.escalatorVersion.id
    );
    if (binding.escalatorVersion.id === -1) {
      duplicateBindings.push(index);
    }
    if (escalatorVersions[binding.escalatorVersion.id]?.domain?.id !== domainId) {
      duplicateBindings.push(index);
    }
    if (firstIndex !== index) {
      duplicateBindings.push(firstIndex);
      duplicateBindings.push(index);
    }
    if (lastIndex !== index) {
      duplicateBindings.push(lastIndex);
      duplicateBindings.push(index);
    }
  });
  return _.uniq(duplicateBindings);
};

//gives duplicates or invalid escalator fault bindings
export const getDuplicateEscalatorFaultBindings = (
  bindings: Api.EscalatorFaultBinding[],
  escalatorVersions: Api.EscalatorVersions,
  domainId: number
): number[] => {
  const duplicateBindings: number[] = [];

  bindings.forEach((binding, index) => {
    const firstIndex = _.findIndex(
      bindings,
      bindingToCompare => bindingToCompare.escalatorVersion.id === binding.escalatorVersion.id
    );
    const lastIndex = _.findLastIndex(
      bindings,
      bindingToCompare => bindingToCompare.escalatorVersion.id === binding.escalatorVersion.id
    );
    if (binding.escalatorVersion.id === -1) {
      duplicateBindings.push(index);
    }
    if (escalatorVersions[binding.escalatorVersion.id]?.domain?.id !== domainId) {
      duplicateBindings.push(index);
    }
    if (firstIndex !== index) {
      duplicateBindings.push(firstIndex);
      duplicateBindings.push(index);
    }
    if (lastIndex !== index) {
      duplicateBindings.push(lastIndex);
      duplicateBindings.push(index);
    }
  });
  return _.uniq(duplicateBindings);
};

export const getDuplicateDoorParameterBindings = (
  bindings: Api.DoorParameterBinding[]
): number[] => {
  const duplicateBindings: number[] = [];

  bindings.forEach((binding, index) => {
    const firstIndex = _.findIndex(
      bindings,
      bindingToCompare => bindingToCompare.doorVersion.id === binding.doorVersion.id
    );
    const lastIndex = _.findLastIndex(
      bindings,
      bindingToCompare => bindingToCompare.doorVersion.id === binding.doorVersion.id
    );
    if (binding.doorVersion.id === -1) {
      duplicateBindings.push(index);
    }
    if (firstIndex !== index) {
      duplicateBindings.push(firstIndex);
      duplicateBindings.push(index);
    }
    if (lastIndex !== index) {
      duplicateBindings.push(lastIndex);
      duplicateBindings.push(index);
    }
  });
  return _.uniq(duplicateBindings);
};

export type PreferredValuesValidationResult = {
  isValid: boolean;
  errorMessage: string;
};

/** Constants for preferredValueTypeEnumValue */
export const PREFERRED_TYPE_MIN_MAX = 1;
export const PREFERRED_TYPE_ENUM_VALUES = 2;

/**
 * Validates preferred values for Drive and Escalator parameters.
 * Validation order:
 *  1. Preferred value digits constraint (based on valueDataFormat.digits)
 *  2. Min/Max constraint when type = PARAM_MIN_MAX (preferredValueTypeEnumValue === 1)
 *  3. Duplicate constraint for Escalator PARAM_ENUM (preferredValueTypeEnumValue === 2)
 */
export const validatePreferredValues = (
  preferredValues: number[],
  preferredValueTypeEnumValue: number,
  valueDataFormat: number,
  isEscalator: boolean
): PreferredValuesValidationResult => {
  const decodedFormat = decodeValueDataFormat(valueDataFormat);
  const digits = decodedFormat.digits ?? 0;

  // Validation 1: Preferred value digits constraint (only for "Number" range)
  if (decodedFormat.range === "Number") {
    const invalidValue = preferredValues.find(v => !isValidDigitCount(v, digits));
    if (invalidValue !== undefined) {
      return {
        isValid: false,
        errorMessage: "The number of digits in the Value data format must match the Preferred values."
      };
    }
  }

  // Validation 2: Min/Max validation for PARAM_MIN_MAX
  if (preferredValueTypeEnumValue === PREFERRED_TYPE_MIN_MAX) {
    // if (preferredValues.length < 2) {
    //   return {
    //     isValid: false,
    //     errorMessage: "PARAM_MIN_MAX requires both a minimum and a maximum value."
    //   };
    // }
    if (preferredValues[0] > preferredValues[1]) {
      return {
        isValid: false,
        errorMessage: "The minimum value must be less than or equal to the maximum value, and vice versa."
      };
    }
  }

  // Validation 3: Duplicate check for Escalator PARAM_ENUM only
  if (isEscalator && preferredValueTypeEnumValue === PREFERRED_TYPE_ENUM_VALUES) {
    const seen = new Set<number>();
    for (const v of preferredValues) {
      if (seen.has(v)) {
        return {
          isValid: false,
          errorMessage: "Preferred values must not contain duplicate entries."
        };
      }
      seen.add(v);
    }
  }

  return { isValid: true, errorMessage: "" };
};

/**
 * Check if a value satisfies the digit constraint.
 * - If digits === 0, only 0 is valid
 * - If digits > 0, |value| must be < 10^digits
 */
const isValidDigitCount = (value: number, digits: number): boolean => {
  if (digits === 0) return value === 0;
  return Math.abs(value) < Math.pow(10, digits);
};

/**
 * Validates only the preferred value digits constraint (validation 1).
 * Use this for showing the error in the Value Data Format section,
 * since min/max and duplicate errors should only show in Preferred Values.
 */
export const validatePreferredValueDigitsOnly = (
  preferredValues: number[],
  valueDataFormat: number
): string | undefined => {
  const decodedFormat = decodeValueDataFormat(valueDataFormat);
  const digits = decodedFormat.digits ?? 0;

  if (decodedFormat.range === "Number") {
    const invalidValue = preferredValues.find(v => !isValidDigitCount(v, digits));
    if (invalidValue !== undefined) {
      return "The number of digits in the Value data format must match the Preferred values.";
    }
  }

  return undefined;
};

// Create an array of string from sw default value
export const createSwDefaultArray = (swDefaultString: string): string[] => {
  if (!swDefaultString || swDefaultString.length === 0) {
    return [];
  } else {
    return swDefaultString.split(";");
  }
};

export const exampleRoles: Api.Role[] = [
  {
    id: 1,
    name: "admin",
    description:
      "Has access to administrative parts of the API, namely, user management. Admin data access is always read+write."
  },
  {
    id: 2,
    name: "read",
    description: "Allows read access to all non-admin data"
  },
  {
    id: 3,
    name: "write",
    description: `Allows write access to all non-admin data. Note that write
     access does not automatically imply read access. It needs to be allowed separately.`
  }
];

export const exampleUsers: Api.User[] = [
  {
    id: 7,
    url: "https://kcofivmmsrui001.konenet.com/localization-api/api/users/7",
    adAccount: "KONENET\\ext_pmakela",
    roles: exampleRoles
  }
];

export const noticationMessages: Api.OptionsDescription = {
  domainGroups: (domainGroup: Api.DomainGroup) => `Domain group: ${domainGroup.name}`,
  domains: (domain: Api.Domain) => `Domain: ${domain.enumName}`,
  features: (feature: Api.Feature) => `Feature: ${feature.swName}`,
  faultComponents: (faultComponent: Api.FaultComponentEnumeration) =>
    `Fault component: ${faultComponent.name}`,
  faultComponentLocalizations: (faultComponent: Api.FaultComponentEnumeration) =>
    `Fault component: ${faultComponent.name}`,
  faults: (fault: Api.AnyFault) => `Fault: ${fault.name}`,
  escalatorFaults: (fault: Api.EscalatorFault) => `Fault: ${fault.name}`,
  apmFaults: (fault: Api.APMFault) => `Fault: ${fault.name}`,
  liftFaults: (fault: Api.LiftFault) => `Fault: ${fault.name}`,
  driveFaults: (fault: Api.DriveFault) => `Fault: ${fault.name}`,
  groupFaults: (fault: Api.GroupFault) => `Fault: ${fault.name}`,
  networkFaults: (fault: Api.NetworkFault) => `Fault: ${fault.name}`,
  monitoringFaults: (fault: Api.MonitoringFault) => `Fault: ${fault.name}`,
  doorFaults: (fault: Api.DoorFault) => `Fault: ${fault.name}`,
  nonKoneFaults: (fault: Api.NonKoneFault) => `Fault: ${fault.name}`,
  signalizationFaults: (fault: Api.SignalizationFault) => `Fault: ${fault.name}`,
  faultLocalizations: (fault: Api.AnyFault) => `Fault: ${fault.name}`,
  parameters: (parameter: Api.AnyParameter) => `Parameter: ${parameter.name}`,
  parameterLocalizations: (parameter: Api.AnyParameter) => `Parameter: ${parameter.name}`,
  liftParameters: (parameter: Api.LiftParameter) => `Parameter: ${parameter.name}`,
  driveParameters: (parameter: Api.DriveParameter) => `Parameter: ${parameter.name}`,
  groupParameters: (parameter: Api.GroupParameter) => `Parameter: ${parameter.name}`,
  safetyParameters: (parameter: Api.SafetyParameter) => `Parameter: ${parameter.name}`,
  escalatorParameters: (parameter: Api.EscalatorParameter) => `Parameter: ${parameter.name}`,
  apmParameters: (parameter: Api.ApmParamters) => `Parameter: ${parameter.name}`,
  doorParameters: (parameter: Api.DoorParameter) => `Parameter: ${parameter.name}`,
  faultPriorities: (faultPriority: Api.FaultPriority) => `Fault priority: ${faultPriority.name}`,
  faultCategories: (faultCategory: Api.FaultCategory) => `Fault category: ${faultCategory.name}`,
  faultCategoryLocalizations: (faultCategory: Api.FaultCategory) =>
    `Fault category: ${faultCategory.name}`,
  parameterCategories: (parameterCategory: Api.ParameterCategory) =>
    `Parameter category: ${parameterCategory.name}`,
  users: (user: Api.User) => `User: ${user.adAccount}`,
  signals: (driveSignal: Api.DriveSignal) => `Drive Signal: ${driveSignal.name}`,
  signalLocalizations: (driveSignal: Api.DriveSignal) => `Drive Signal: ${driveSignal.name}`,
  driveTests: (DriveTest: Api.DriveTest) => `Drive Test: ${DriveTest.name}`,
  driveTestLocalizations: (DriveTest: Api.DriveTest) => `Drive Test: ${DriveTest.name}`,
  signalGroups: (driveSignalGroup: Api.DriveSignalGroup) =>
    `Drive Signal Group: ${driveSignalGroup.name}`,
  signalGroupLocalizations: (driveSignalGroup: Api.DriveSignalGroup) =>
    `Drive Signal Group: ${driveSignalGroup.name}`,
  escalatorRTDs: (escalatorRTD: Api.EscalatorRTD) => `Escalator RTD: ${escalatorRTD.name}`,
  escalatorRTDLocalizations: (escalatorRTD: Api.EscalatorRTD) =>
    `Escalator RTD: ${escalatorRTD.name}`,
  escalatorRTDGroups: (escalatorRTDGroup: Api.EscalatorRTDGroup) =>
    `Escalator RTD Group: ${escalatorRTDGroup.name}`,
  escalatorRTDGroupLocalizations: (escalatorRTDGroup: Api.EscalatorRTDGroup) =>
    `Escalator RTD Group: ${escalatorRTDGroup.name}`,
  roles: (role: Api.Role) => `Role: ${role.name}`,
  userGroups: (usergroup: Api.UserGroup) => `User group: ${usergroup.enumName}`,
  kceVersions: (kceVersion: Api.KceVersion) => `KCE Version: ${kceVersion.name}`,
  driveVersions: (driveVersion: Api.DriveVersion) => `Drive Version: ${driveVersion.year}`,
  kceProducts: (kceProduct: Api.KceProduct) => `KCE Product: ${kceProduct.productName}`,
  messageTypes: (messageType: Api.MessageType) => `Message Type: ${messageType.enumName}`,
  eventClasses: (enumeration: Api.BaseEnumeration) => `Event Class: ${enumeration.enumName}`,
  eventPids: (enumeration: Api.BaseEnumeration) => `Event PID: ${enumeration.enumName}`,
  eventTypes: (enumeration: Api.BaseEnumeration) => `Event Type: ${enumeration.enumName}`,
  extCommands: (enumeration: Api.BaseEnumeration) => `Ext. Command: ${enumeration.enumName}`,
  ioBoardTypes: (enumeration: Api.BaseEnumeration) => `IO Board Type: ${enumeration.enumName}`,
  ioCpuEnums: (enumeration: Api.BaseEnumeration) => `IO CPU: ${enumeration.enumName}`,
  ioOptions: (enumeration: Api.ioOption) => `IO Option: ${enumeration.enumName}`,
  ioNetworks: (enumeration: Api.BaseEnumeration) => `IO Network: ${enumeration.enumName}`,
  ioDataTypes: (enumeration: Api.BaseEnumeration) => `IO DataType: ${enumeration.enumName}`,
  ioOptionsEnumType: (enumeration: Api.IoOptionsEnumType) =>
    `IO Options EnumType: ${enumeration.enumTypeName}`,
  ioBasicDataTypes: (enumeration: Api.IoBasicDataType) =>
    `IO Basic Datatype: ${enumeration.enumName}`,
  iodataSections: (enumeration: Api.IodataSections) => `IO Data Sections: ${enumeration.name}`,
  enumerations: (enumeration: Api.CommonEnumeration) => `Enumeration: ${enumeration.name}`,
  enumerationItems: (enumerationItem: Api.CommonEnumerationItem) =>
    `Enumerator: ${enumerationItem.enumName}`,
  standardBoards: (standardBoard: Api.StandardBoard) => `Standard Board: ${standardBoard.boardType.enumName} with Revision: ${standardBoard.revision}`,
  standardNodes: (standardNode: Api.StandardNode) => `Standard Node: ${standardNode.id}`,
  decks: (deck: Api.BaseEnumeration) => `Deck: ${deck.enumName}`,
  sides: (side: Api.BaseEnumeration) => `Side: ${side.enumName}`,
  electrifications: (electrification: Api.BaseEnumeration) =>
    `Electrification: ${electrification.enumName}`,

  standardActions: (action: Api.StandardAction) => `Standard Action: ${action.id}  `,
  ioConfigurations: (action: Api.IoConfiguration) => `IO configuration: ${action.friendlyName}`,
  standardPoints: (point: Api.StandardPoint) => `Standard Point: ${point.ioname}`,
  ioBoards: (board: Api.IoBoard) => `IO Board: ${board.standard.boardType.enumName} with Revision:${board.standard.revision}`,
  ionodes: (node: Api.IoNode) => `IO Node: ${node.id}`,
  ionodePoints: (point: Api.IoNodePoint) => `IO Point: ${point.ioname}`,
  aadgroups: (aadgroup: Api.AADGroup) => `aadGroup: ${aadgroup.name}`,
  ionodePointActions: (action: Api.IoNodePointAction) => `IO Action: ${action.id}`,
  translations: (action: Api.Locale) => `locale ${action.locale}`,
  localizationids: (action: Api.Locale) => `locale ${action.locale}`,
  parseBinaryIoConfig: (action: ArrayBuffer) => `result: ${action}`,
  doorEventClasses: (enumeration: Api.EventEnumeration) =>
    `Door Event Class: ${enumeration.enumName}`,
  doorEventTypes: (enumeration: Api.EventTypesEnum) => `Door Event Type: ${enumeration.enumName}`,
  doorEventPids: (enumeration: Api.EventEnumeration) => `Door Event PID: ${enumeration.enumName}`,
  doorExtCommands: (enumeration: Api.EventEnumeration) =>
    `Door Ext. Command: ${enumeration.enumName}`,
  EscalatorEventClasses: (enumeration: Api.EventEnumeration) =>
    `Escalator Event Class: ${enumeration.enumName}`,
  EscalatorEventTypes: (enumeration: Api.EventEnumeration) =>
    `Escalator Event Type: ${enumeration.enumName}`,
  EscalatorEventPids: (enumeration: Api.EventEnumeration) =>
    `Escalator Event PID: ${enumeration.enumName}`,
  EscalatorExtCommands: (enumeration: Api.EventEnumeration) =>
    `Escalator Ext. Command: ${enumeration.enumName}`,
  EscalatorRequestTypes: (enumeration: Api.EventEnumeration) =>
    `Escalator Request Type: ${enumeration.enumName}`,
  DoorVersions: (enumeration: Api.DoorVersion) => `Door Version: ${enumeration.versionName}`,
  EscalatorVersions: (escalatorVersion: Api.EscalatorVersion) =>
    `Escalator Version: ${escalatorVersion.versionName}`,
  dataActs: (dataAct: Api.DataActCode) => `Data Act Code: ${dataAct.name}`,
  monitoringFaultCategories: (faultCategory: Api.FaultCategory) =>
    `Monitoring Fault Category: ${faultCategory.name}`,
  escalatorDataActs: (dataAct: Api.EscalatorDataActCode) => `Escalator Data Act Code: ${dataAct.name}`,
  grandNorms: (grandNorm: Api.GrandNorm) => `Grand Norm: ${grandNorm.name}`,
  escalatorEventLogs: (escalatorEventLog: Api.EscalatorEventLog) => `Escalator Event Log: ${escalatorEventLog.name}`,
  escalatorEventLogLocalizations: (escalatorEventLog: Api.EscalatorEventLog) => `Escalator Event Log: ${escalatorEventLog.name}`
};

export const parameterCategoryHasChildren = (
  parentId: number,
  parameterCategories: Api.ParameterCategories
): boolean => {
  const children = _.values(parameterCategories).filter(
    row => row.parentCategory && row.parentCategory.id === parentId
  );

  return children.length > 0;
};

export const getDomainByName = (domains: Api.Domains, domainName: string): Api.Domain => {
  if (domainName) {
    for (const i in domains) {
      if (domains[i].enumName === domainName) {
        return domains[i];
      }
    }
  }
  return {} as Api.Domain;
};

export const driveSignalGroupHasChildren = (
  parentId: number,
  signals: Api.DriveSignal[]
): boolean => {
  const children = _.values(signals).filter(row => row.signalGroupEnumValue === parentId);

  return children.length > 0;
};

export const driveVersionHasChildren = (
  driveVersionId: number,
  driveParameters: Api.DriveParameter[]
): boolean => {
  const kceDriveBindingsVersions = driveParameters.map(param =>
    param.driveParameterBindings.map(binding => binding.driveVersion)
  );
  const kceDriveUsedVersions = _.flatten([...kceDriveBindingsVersions]);
  const isdriveVersionUsed = kceDriveUsedVersions.find(version => version.id === driveVersionId);
  return isdriveVersionUsed ? true : false;
};

export const escalatorVersionHasChildren = (
  escalatorVersionId: number,
  escalatorParameters: Api.EscalatorParameter[],
  escalatorFaults?: Api.EscalatorFault[]
): boolean => {
  const escalatorBindingsVersions = escalatorParameters.map(param =>
    param.escalatorParameterBindings.map(binding => binding.escalatorVersion)
  );
  const escalatorUsedVersions = _.flatten([...escalatorBindingsVersions]);
  const isescalatorVersionUsed = escalatorUsedVersions.find(
    version => version.id === escalatorVersionId
  );
  if (isescalatorVersionUsed) {
    return true;
  }
  if (escalatorFaults) {
    const faultBindingsVersions = escalatorFaults.map(fault =>
      (fault.escalatorFaultBindings || []).map(binding => binding.escalatorVersion)
    );
    const faultUsedVersions = _.flatten([...faultBindingsVersions]);
    return !!faultUsedVersions.find(version => version.id === escalatorVersionId);
  }
  return false;
};

export const escalatorRTDGroupHasChildren = (
  parentId: number,
  rtds: Api.EscalatorRTD[]
): boolean => {
  const children = _.values(rtds).filter(row => row.rtdGroupEnumValue === parentId);

  return children.length > 0;
};

export const doorVersionHasChildren = (
  doorVersionId: number,
  doorParameters: Api.DoorParameter[]
): boolean => {
  const doorBindingsVersions = doorParameters.map(param =>
    param.doorParameterBindings.map(binding => binding.doorVersion)
  );
  const doorUsedVersions = _.flatten([...doorBindingsVersions]);
  const isdoorVersionUsed = doorUsedVersions.find(version => version.id === doorVersionId);
  return isdoorVersionUsed ? true : false;
};

export const liftVersionHasChildren = (
  liftVersionId: number,
  liftParameters: Api.LiftParameter[],
  ioConfigurations: Api.IoConfiguration[],
  faults: Api.AnyFault[]
): boolean => {
  const ioConfigVersions = ioConfigurations.map(config => config.kceVersion);
  const ioConfigVersionsUsed = ioConfigVersions.find(version => version.id === liftVersionId);
  if (ioConfigVersionsUsed) {
    return true;
  } else {
    const kceBindingsVersions = liftParameters.map(param =>
      param.liftParameterBindings.map(binding => binding.kceVersion)
    );
    const kceBindingsVersionsList = _.flatten([...kceBindingsVersions]);
    const kceBindingsVersionsUsed = kceBindingsVersionsList.find(
      version => version.id === liftVersionId
    );
    if (kceBindingsVersionsUsed) {
      return true;
    } else {
      const kceDefaultBindVersions = liftParameters.map(param =>
        param.liftParameterValues.map(binding => binding.kceVersion)
      );
      const kceDefaultBindVersionsList = _.flatten([...kceDefaultBindVersions]);
      const kceDefaultBindVersionsUsed = kceDefaultBindVersionsList.find(
        version => version.id === liftVersionId
      );
      if (kceDefaultBindVersionsUsed) {
        return true;
      } else {
        const isNotUsedInfaults =
          _.values(faults).find(fault => {
            if (
              (fault as Api.LiftFault | Api.APMFault).validFromVersion?.id === liftVersionId ||
              (fault as Api.LiftFault | Api.APMFault).validUntilVersion?.id === liftVersionId
            ) {
              return fault;
            }
          }) === undefined; //returns true if not used in any faults
        if (!isNotUsedInfaults) {
          return true;
        }
      }
    }
    return false; //means no children
  }
};

export const liftProductHasChildren = (
  liftProductNumber: number,
  liftParameters: Api.LiftParameter[],
  ioConfigurations: Api.IoConfiguration[]
): boolean => {
  const ioConfigProducts = ioConfigurations.map(config => config.kceProduct);
  const ioConfigProductsUsed = ioConfigProducts.find(
    product => product.productNumber === liftProductNumber
  );
  if (ioConfigProductsUsed) {
    return true;
  } else {
    const kceBindingsProducts = liftParameters.map(param =>
      param.liftParameterBindings.map(binding => binding.kceProduct)
    );
    const kceBindingsProductsList = _.flatten([...kceBindingsProducts]);
    const kceBindingsProductsUsed = kceBindingsProductsList.find(
      product => product.id === liftProductNumber
    ); //here Product.id holds pdt number
    if (kceBindingsProductsUsed) {
      return true;
    } else {
      const kceDefaultBindProducts = liftParameters.map(param =>
        param.liftParameterValues.map(binding => binding.kceProduct)
      );
      const kceDefaultBindProductsList = _.flatten([...kceDefaultBindProducts]);
      const kceDefaultBindProductsUsed = kceDefaultBindProductsList.find(
        product => product.id === liftProductNumber
      ); //here Product.id holds pdt number
      if (kceDefaultBindProductsUsed) {
        return true;
      }
    }
    return false;
  }
};

export const filterUserGroupParameters = (
  parameters: Api.LiftParameter[] | Api.DoorParameter[] | Api.EscalatorParameter[],
  filterType: number
): Api.LiftParameter[] | Api.DoorParameter[] | Api.EscalatorParameter[] =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _.values(parameters).filter((userGroups: any) =>
    userGroups.permissions.some(
      (permission: Api.Permission) =>
        permission.userGroup.id === filterType &&
        (permission.execute || permission.write || permission.read)
    )
  ) as Api.LiftParameter[] | Api.DoorParameter[] | Api.EscalatorParameter[];
